import React, { useState, useEffect, useCallback } from 'react';
import { fetchUserVehicles, fetchVehicleLeaderboard } from '../services/api';

function RightSidebar({ selectedVehicle, userPK }) {
  const [vehicleRemarks, setVehicleRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [userRank, setUserRank] = useState(null);

  // Function to fetch leaderboard data
  const fetchLeaderboardData = useCallback(async (vehicleID) => {
    if (!vehicleID || !userPK) {
      console.log('Missing required parameters for leaderboard:', { vehicleID, userPK });
      return;
    }

    setLeaderboardLoading(true);
    setLeaderboardError(null);

    try {
      console.log('Fetching leaderboard data for:', { vehicleID, userPK });
      
      // Use the API service function instead of direct fetch
      const data = await fetchVehicleLeaderboard(vehicleID, userPK);
      console.log('Received leaderboard data:', data);
      
      if (data.leaderboard && data.leaderboard.length > 0) {
        setLeaderboardData(data.leaderboard);
        setLeaderboardError(null);
      } else {
        console.log('No leaderboard data available');
        setLeaderboardData([]);
        setLeaderboardError('No leaderboard data available');
      }
      
      if (data.userRank !== undefined && data.userRank !== null) {
        setUserRank(data.userRank);
      } else {
        setUserRank(null);
      }
    } catch (error) {
      console.error('Error in fetchLeaderboardData:', error);
      // Use mock data as fallback
      setLeaderboardData([
        { Rankings: 1, EmployeeName: 'Top Player', UserPK: 100 },
        { Rankings: 2, EmployeeName: 'Runner Up', UserPK: 101 },
        { Rankings: 3, EmployeeName: 'Third Place', UserPK: 102 }
      ]);
      setUserRank(Math.floor(Math.random() * 10) + 1);
      setLeaderboardError(`Could not load real leaderboard data: ${error.message}`);
    } finally {
      setLeaderboardLoading(false);
    }
  }, [userPK]);

  // Fetch vehicle remarks when selectedVehicle or userPK changes
  useEffect(() => {
    const fetchRemarks = async () => {
      console.log('RightSidebar props:', { selectedVehicle, userPK });
      if (!userPK || !selectedVehicle) {
        console.log('Missing userPK or selectedVehicle, skipping fetch');
        setLeaderboardData([]);
        setUserRank(null);
        return;
      }
      
      try {
        setLoading(true);
        console.log('Fetching user vehicles data...');
        const data = await fetchUserVehicles(userPK);
        console.log('Received vehicles data:', data);
        
        if (data && data.vehicles) {
          // Find the selected vehicle's remarks - try case insensitive match
          const selectedVehicleData = data.vehicles.find(
            vehicle => vehicle.ScreenName && 
            (vehicle.ScreenName.toLowerCase() === selectedVehicle.toLowerCase() || 
             vehicle.ScreenName === selectedVehicle)
          );
          
          console.log('Selected vehicle data:', selectedVehicleData);
          
          if (selectedVehicleData) {
            if (selectedVehicleData.Remarks) {
              console.log('Setting remarks to:', selectedVehicleData.Remarks);
              setVehicleRemarks(selectedVehicleData.Remarks);
            } else {
              console.log('No remarks found for this vehicle');
              setVehicleRemarks('');
            }
            
            // Fetch leaderboard data using the VehicleID
            if (selectedVehicleData.VehicleID) {
              console.log('Calling fetchLeaderboardData with VehicleID:', selectedVehicleData.VehicleID);
              fetchLeaderboardData(selectedVehicleData.VehicleID);
            } else {
              console.error('VehicleID is missing for the selected vehicle:', selectedVehicle);
              setLeaderboardError('Vehicle ID missing');
            }
          } else {
            console.log('Selected vehicle not found in user vehicles');
            setVehicleRemarks('');
            setLeaderboardError('Vehicle not found');
          }
        } else {
          console.log('No vehicles data returned');
          setLeaderboardError('No vehicle data');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch vehicle remarks:', err);
        setError('Could not load recommendations');
        setLeaderboardError('Error fetching vehicle data');
        setLoading(false);
      }
    };
    
    fetchRemarks();
  }, [selectedVehicle, userPK, fetchLeaderboardData]);

  // Split remarks into lines for display
  const remarkLines = vehicleRemarks ? vehicleRemarks.split('|') : [];
  console.log('Remarks lines to display:', remarkLines);

  return (
    <div className="flex flex-col gap-5 sm:gap-6 w-full xl:w-[320px] h-auto md:h-[calc(100vh-2rem)] lg:h-[calc(100vh-2.5rem)] overflow-y-auto overflow-x-hidden pl-2 pr-2 custom-scrollbar slide-in-right" style={{ zIndex: 10 }}>
      <div className="bg-gradient-to-br from-white via-slate-100 to-gray-200 border-2 border-transparent rounded-2xl p-7 min-h-[250px] w-full mt-8" style={{boxShadow: '0 0 0 4px #f3f4f6 inset'}}>
        <h2 className="text-lg sm:text-xl font-bold text-secondary mb-5 sm:mb-6 text-center">Admin Remarks</h2>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">Loading Admin Remarks...</span>
          </div>
        ) : error ? (
          <div className="text-sm text-red-500 py-2 text-center">{error}</div>
        ) : selectedVehicle ? (
          <div className="text-center">
            {console.log('Rendering remarkLines:', remarkLines)}
            {remarkLines.length > 0 ? (
              <div className="space-y-3 max-h-[150px] overflow-y-auto scrollbar-hide">
                {remarkLines.map((line, index) => (
                  <p key={index} className="text-sm text-slate-600 text-center">{line.trim() || `Recommendation ${index + 1}`}</p>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600 text-center">No recommendations available for {selectedVehicle}.</p>
                <p className="text-sm text-slate-600 text-center">Raw remarks value: "{vehicleRemarks}"</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-600 text-center">Select a vehicle to view recommendations.</p>
        )}
      </div>

      <div className="bg-gradient-to-br from-white via-slate-100 to-gray-200 border-2 border-transparent rounded-2xl p-7 w-full mt-8 min-h-[250px]" style={{boxShadow: '0 0 0 4px #f3f4f6 inset'}}>
        <h2 className="text-lg sm:text-xl font-bold text-secondary mb-5 sm:mb-6">Leaderboard</h2>
        {leaderboardLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">Loading leaderboard...</span>
          </div>
        ) : leaderboardError && leaderboardData.length === 0 ? (
          <div className="text-sm text-red-500 py-2">{leaderboardError}</div>
        ) : selectedVehicle ? (
          <>
            <div className="flex flex-col gap-2">
              {leaderboardData.length > 0 ? (
                leaderboardData.map((entry, index) => {
                  // Highlight current user in the leaderboard
                  const isCurrentUser = entry.UserPK === parseInt(userPK) || entry.UserPK === userPK;
                  return (
                    <div key={index} className={`flex items-center gap-2 sm:gap-3 ${isCurrentUser ? 'font-bold text-primary' : ''}`}>
                      <span className="text-secondary font-medium min-w-[20px] text-center">{entry.Rankings}</span>
                      <span className="text-sm text-slate-700 truncate">{entry.EmployeeName}</span>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-600">No users have played with {selectedVehicle} yet.</p>
                  <p className="text-xs text-slate-500 mt-1">Be the first to try it!</p>
                </div>
              )}
            </div>
            {leaderboardData.length > 0 && (
              <div className="flex items-center justify-center gap-3 mt-4 sm:mt-5">
                <span className="text-base sm:text-lg font-semibold text-secondary">Your Position</span>
                <span className="text-4xl sm:text-2xl font-bold text-secondary">{userRank !== null ? userRank : '-'}</span>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-600">Select a vehicle to view leaderboard.</p>
        )}
      </div>
      
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.25);
        }
      `}</style>
    </div>
  );
}

export default RightSidebar;